/** Pure helpers backing the staged import-review wizard's step components
 *  (Tasks 5–9). No React, no server calls — testable in isolation. */
import type { ExpectedField, ImportBatchRow } from '@discovr/types';
import { isOfferedOnPlatform } from '@discovr/types';
import { parseAmount } from './amountParse';

// ── Sheet-preview column sizing ───────────────────────────────────────────
// The client-side preview table (ImportUploadStep) renders whatever raw
// header text is in the uploaded sheet — this runs before mapping, so we
// don't yet know which BE field a column will become. `estimatePreviewColumnWidths`
// still gives each column a sensible max-width by combining three signals:
// (1) the header text itself (a column is never narrower than its own
// label), (2) this app's own field-length limits where the header happens to
// match a known field key (e.g. first_name/last_name cap at 100 chars — see
// maxNameLength in the BE's import_review_rules.go), and (3) a conventional
// spreadsheet sizing fallback by inferred type (numeric columns stay narrow,
// free text gets more room) for anything unrecognized. A hard ceiling keeps
// any one column (a maxed-out 100-char name, a very long unmatched header)
// from dominating the table — real overflow still shows via truncation +
// a title tooltip, not a runaway-wide column.
const KNOWN_FIELD_MAX_CHARS: Record<string, number> = {
  username: 30, // Instagram/YouTube handle length convention
  first_name: 100,
  last_name: 100,
  engagement_rate: 8, // e.g. "12.34%"
  avg_views: 12, // e.g. "999,999,999"
};
const NUMERIC_MAX_CHARS = 12;
const TEXT_MAX_CHARS = 40;
const ABSOLUTE_MAX_CHARS = 40;
const MIN_CHARS = 6;
const CHAR_PX = 7; // ~average glyph width for the preview table's 12px text
const CELL_PADDING_PX = 24; // px-3 on both sides
const MIN_COLUMN_PX = 72;
const MAX_COLUMN_PX = 320;

function normalizeHeaderKey(header: string): string {
  return header.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function looksNumeric(value: string): boolean {
  return value === '' || /^-?[\d,.]+%?$/.test(value);
}

function maxCharsForColumn(header: string, values: string[]): number {
  const known = KNOWN_FIELD_MAX_CHARS[normalizeHeaderKey(header)];
  if (known !== undefined) return known;
  return values.length > 0 && values.every(looksNumeric) ? NUMERIC_MAX_CHARS : TEXT_MAX_CHARS;
}

/** One max-width in px per column, in header order. */
export function estimatePreviewColumnWidths(rows: string[][]): number[] {
  const header = rows[0] ?? [];
  return header.map((headerText, colIndex) => {
    const values = rows.slice(1).map((row) => row[colIndex] ?? '');
    const maxChars = maxCharsForColumn(headerText, values);
    const longestValueChars = values.reduce((max, v) => Math.max(max, v.length), 0);
    const chars = Math.min(
      Math.max(headerText.length, Math.min(longestValueChars, maxChars)),
      ABSOLUTE_MAX_CHARS,
    );
    const px = Math.max(chars, MIN_CHARS) * CHAR_PX + CELL_PADDING_PX;
    return Math.min(Math.max(px, MIN_COLUMN_PX), MAX_COLUMN_PX);
  });
}

/** A field's label in the mapping dropdown and the "Fix here" inline
 *  editor's column header — both are dense, fixed-width contexts where
 *  engagement_rate's full label doesn't comfortably fit. Error messages
 *  keep the field's real label. */
export function compactFieldLabel(f: ExpectedField): string {
  return f.key === 'engagement_rate' ? 'eng_rate' : f.label;
}

/** Shortens a machine field key (or a raw sheet header, matched
 *  case-insensitively) to the same recommended spelling everywhere it's
 *  shown on the upload step: column-name chips, the downloadable template's
 *  header row, AND the client-side sheet-preview table. Every shortened
 *  spelling is one the BE's own fieldKeywords/platformShortAlias already
 *  accepts (admin_import_preview_handler.go / admin_handler.go), so a sheet
 *  built from our own template — or any sheet already using these — round-
 *  trips through mapping correctly, not just displaying them cosmetically:
 *  - engagement_rate -> eng_rate
 *  - any instagram_*  -> ig_*   (mirrors platformShortAlias)
 *  - any youtube_*    -> yt_*   (mirrors platformShortAlias)
 */
export function compactKey(key: string): string {
  const normalized = key.trim().toLowerCase();
  if (normalized === 'engagement_rate') return 'eng_rate';
  if (normalized.startsWith('instagram_')) return `ig_${key.slice('instagram_'.length)}`;
  if (normalized.startsWith('youtube_')) return `yt_${key.slice('youtube_'.length)}`;
  return key;
}

/** A field's machine key as shown in the upload step's column-name chips
 *  and the downloadable template's header row — see `compactKey`. */
export function compactFieldKey(f: ExpectedField): string {
  return compactKey(f.key);
}

/** Fields the roster-conflict comparison and the Confirm-step diff can never
 *  meaningfully show a "current" value for:
 *  - username — already the card's own title, identical on both sides by
 *    construction (it's what matched the row).
 *  - first_name/last_name — these live on the creator's User record, not on
 *    the roster entry itself, and nothing in the roster-review flow fetches
 *    it (a deliberate scope call, not an oversight — see the BE's
 *    rosterEntryCurrentValues). Showing "— → John" here would look like a
 *    real change when it's actually just a value we never had. */
const NOT_COMPARABLE_FIELDS = new Set(['username', 'first_name', 'last_name']);

/** Which fields to show in a roster-conflict row's current-vs-new value
 *  comparison.
 *  - See NOT_COMPARABLE_FIELDS above.
 *  - Deliverable prices are excluded when the matched entry is locked: an
 *    Overwrite there never touches them (RosterMatch.locked), so listing
 *    them as a "current vs new" pair would show a comparison that can't
 *    actually happen — stat fields always update regardless of lock state.
 *  - Only fields the SHEET carries a value for are shown. A column the sheet
 *    never had, or a cell left blank, writes nothing at commit (a blank price
 *    preserves the deliverable; a blank optional stat leaves the fetched one
 *    alone) — so listing "543000 → —" would describe a change that can't
 *    happen and make every fetched creator look like a conflict on a
 *    re-import. The sheet filling in a previously-blank roster value still
 *    shows, since that IS a real change. Mirrors the BE's
 *    rosterMatchIsIdentical. */
export function rosterComparisonFields(
  expectedFields: ExpectedField[],
  locked: boolean,
  _currentValues: Record<string, string>,
  newValues: Record<string, string>,
): ExpectedField[] {
  return expectedFields.filter((f) => {
    if (NOT_COMPARABLE_FIELDS.has(f.key) || (locked && f.isDeliverable)) return false;
    return Boolean(newValues[f.key]);
  });
}

/** The mapping step's "First value" preview, cleaned the same way the import
 *  will actually store the cell — so the admin sees the real result the moment
 *  they pick a field, not the raw sheet text:
 *  - username: a leading "@" is dropped (mirrors the BE's NormalizeSocialHandle).
 *  - deliverable price: parsed with the same shared parseAmount everything
 *    else in this file uses, so "₹5,000", "12k", and "₮5,000" all preview as
 *    their expanded numeric form ("5000", "12000", "5000"). A cell parseAmount
 *    can't take (a range, a date, free text) is shown as-is — import skips it
 *    rather than cleaning it.
 *  - any other field, or an unmapped column: the raw value, unchanged. */
export function cleanedFirstValue(fieldKey: string, isDeliverable: boolean, raw: string): string {
  const value = raw.trim();
  if (!fieldKey || value === '') return raw;
  if (fieldKey === 'username') return value.replace(/^@+/, '');
  if (isDeliverable) {
    const n = parseAmount(value, 'price');
    if (n === null) return raw;
    // Round to at most 2 decimal places (paise-level precision — the finest
    // a deliverable price is ever stored at) before stringifying. A
    // decimal-suffix input (e.g. "1.1 lakh" -> 1.1 * 100000) can land on an
    // IEEE-754 artifact like 110000.00000000001 in floating-point
    // arithmetic — String()'ing that directly would show the ugly float
    // instead of the clean whole number the admin expects. Rounding first
    // clears the float noise; JS numbers drop trailing zeros on their own
    // (String(110000) is "110000", not "110000.00"), so no separate
    // zero-stripping step is needed.
    return String(Math.round(n * 100) / 100);
  }
  return raw;
}

/** Per-column dropdown options for the mapping step. A field already picked
 *  for a DIFFERENT column is excluded (spec §4: "the field can only be
 *  assigned once by construction, no server round-trip needed"). */
export function availableFieldsFor(
  header: string,
  expectedFields: ExpectedField[],
  mapping: Record<string, string>,
): ExpectedField[] {
  const takenElsewhere = new Set(
    Object.entries(mapping)
      .filter(([h, key]) => h !== header && key)
      .map(([, key]) => key),
  );
  return expectedFields.filter((f) => !takenElsewhere.has(f.key));
}

/** Continue is disabled until every required field (username / engagement_rate
 *  / avg_views) has a column mapped to it. */
export function isMappingComplete(
  expectedFields: ExpectedField[],
  mapping: Record<string, string>,
): boolean {
  const mappedKeys = new Set(Object.values(mapping).filter(Boolean));
  return expectedFields.filter((f) => f.required).every((f) => mappedKeys.has(f.key));
}

export function missingRequiredFields(
  expectedFields: ExpectedField[],
  mapping: Record<string, string>,
): ExpectedField[] {
  const mappedKeys = new Set(Object.values(mapping).filter(Boolean));
  return expectedFields.filter((f) => f.required && !mappedKeys.has(f.key));
}

/** Duplicate-in-sheet groups (spec §3.1: DuplicateGroupSize > 1), keyed by
 *  usernameKey. A group is only ever rendered once resolution is needed. */
export function duplicateGroups(rows: ImportBatchRow[]): Map<string, ImportBatchRow[]> {
  const groups = new Map<string, ImportBatchRow[]>();
  for (const row of rows) {
    if (row.duplicateGroupSize <= 1) continue;
    groups.set(row.usernameKey, [...(groups.get(row.usernameKey) ?? []), row]);
  }
  return groups;
}

/** A duplicate-group member with validation errors can't be picked as the
 *  keeper (spec §5 edge case 2) — the admin must fix it or pick another. */
export function isRowPickable(row: ImportBatchRow): boolean {
  return Object.keys(row.validationErrors).length === 0;
}

/** True when a duplicate group's members carry the exact same values for
 *  every field any of them has — i.e. the same creator's row reported more
 *  than once with no actual difference, not a genuine "which one is right"
 *  conflict. Compares the union of value keys any member has, so a field
 *  present on only one row still counts as a real difference. Groups of 1
 *  trivially match (nothing to compare against). */
export function duplicateGroupValuesMatch(members: ImportBatchRow[]): boolean {
  if (members.length <= 1) return true;
  const keys = new Set<string>();
  members.forEach((m) => Object.keys(m.values).forEach((k) => keys.add(k)));
  const first = members[0] as ImportBatchRow;
  return members.every((m) => [...keys].every((k) => (m.values[k] ?? '') === (first.values[k] ?? '')));
}

const MAX_NAME_LENGTH = 100;

/** Reports whether a deliverable-price cell holds something that reads as
 *  an amount but amountParse can't take, OR a negative number (no longer a
 *  valid "remove" convention — the literal word "remove" is, since a
 *  negative sign is one typo away from a valid positive price). Only cells
 *  containing a digit qualify — a purely verbal cell ("free" or "remove")
 *  or a percentage ("50%") is a documented "skip"/"remove" convention, not
 *  a typo. Mirrors the backend's looksLikeUnreadablePrice
 *  (import_review_rules.go) exactly. */
function looksLikeUnreadablePriceClient(key: string, raw: string, campaignPlatform: string | undefined): boolean {
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed.endsWith('%')) return false;
  if (!campaignPlatform || !isOfferedOnPlatform(key, campaignPlatform)) return false;
  if (!/\p{Nd}/u.test(trimmed)) return false;
  const n = parseAmount(trimmed, 'price');
  return n === null || n < 0;
}

/** Mirrors enough of the BE's NormalizeSocialHandle (internal/common/
 *  social_handle.go) to catch a URL that resolves to NO handle at all —
 *  e.g. a bare domain like "https://instagram.com/" has a non-empty string
 *  but no actual handle, and the BE rejects it as missing. A bare (non-URL)
 *  string always passes through unchanged, same as the BE. An unparseable
 *  URL also passes through unchanged (the BE only replaces `s` when
 *  `url.Parse` succeeds), so it's never treated as empty here either. */
function extractHandleClient(raw: string): string {
  const s = raw.trim();
  if (s === '' || (!s.startsWith('http://') && !s.startsWith('https://'))) return s;
  try {
    const segments = new URL(s).pathname.split('/').filter(Boolean);
    return segments.find((seg) => seg.startsWith('@')) ?? segments[0] ?? '';
  } catch {
    return s;
  }
}

/** Mirrors the BE's classifyUsernamePlatform (import_rules.go): a bare
 *  handle or unrecognized host is never a mismatch — only a recognized
 *  Instagram/YouTube profile URL for the OTHER platform is. */
function classifyUsernamePlatform(raw: string, campaignPlatform: string): { ok: boolean; detected: string } {
  const s = raw.trim();
  if (!s.startsWith('http://') && !s.startsWith('https://')) return { ok: true, detected: '' };
  let host: string;
  try {
    host = new URL(s).hostname.toLowerCase();
  } catch {
    return { ok: true, detected: '' };
  }
  host = host.replace(/^www\./, '').replace(/^m\./, '');
  let detected: string;
  switch (host) {
    case 'instagram.com':
    case 'instagr.am':
      detected = 'instagram';
      break;
    case 'youtube.com':
    case 'youtu.be':
      detected = 'youtube';
      break;
    default:
      return { ok: true, detected: '' };
  }
  return detected === campaignPlatform ? { ok: true, detected: '' } : { ok: false, detected };
}

/** The first path segment of a same-platform URL that points at CONTENT
 *  (a post/reel/video), not a profile — mirrors the BE's
 *  nonProfilePathPrefixes/isContentURL (import_rules.go). */
const NON_PROFILE_PATH_PREFIXES = new Set([
  'p', 'reel', 'reels', 'tv', 'stories', 'explore', // Instagram
  'watch', 'shorts', 'playlist', 'embed', 'results', // YouTube
]);

function isContentURL(raw: string): boolean {
  const s = raw.trim();
  if (!s.startsWith('http://') && !s.startsWith('https://')) return false;
  let pathname: string;
  try {
    pathname = new URL(s).pathname;
  } catch {
    return false;
  }
  const first = pathname.replace(/^\/+|\/+$/g, '').split('/')[0] ?? '';
  return NON_PROFILE_PATH_PREFIXES.has(first.toLowerCase());
}

function profileURLGuidance(campaignPlatform: string | undefined, isContent = false): string {
  const subject = campaignPlatform === 'youtube'
    ? 'a YouTube'
    : campaignPlatform === 'instagram'
      ? 'an Instagram'
      : 'a creator';
  const message = `Enter ${subject} profile URL or username`;
  return isContent ? `${message}, not a post or video URL.` : `${message}.`;
}

/** Mirrors the BE's validateBatchRowValues for the 5 fields it actually
 *  validates (import_review_rules.go) — the only fields the "Needs fixing"
 *  editor ever shows a red/green border for. Returns null when the CURRENT
 *  draft value is valid, so the editor can flip a field from red to green
 *  live as the admin types, instead of only finding out after Save fix
 *  round-trips to the server. Two BE-mirroring gates: campaignPlatform gates
 *  deliverable-key coverage the same way the BE's OfferedOnPlatform check
 *  does — omitted (e.g. before the campaign has loaded), deliverable cells
 *  are left unvalidated client-side (server still catches it on submit).
 *  `required` gates engagement_rate/avg_views the same way the BE's
 *  requireMetricsForPlatform does — explicitly false (e.g. Instagram, where
 *  these aren't required) skips the >0 check for a blank cell instead of
 *  flagging it as an error the server wouldn't actually raise. Omitted
 *  (unknown yet) behaves like the old, stricter default and still requires
 *  a value. */
export function clientFieldError(key: string, value: string, campaignPlatform?: string, required?: boolean): string | null {
  switch (key) {
    case 'username': {
      if (extractHandleClient(value) === '') return profileURLGuidance(campaignPlatform);
      if (campaignPlatform) {
        const { ok } = classifyUsernamePlatform(value, campaignPlatform);
        if (!ok) return profileURLGuidance(campaignPlatform);
      }
      return isContentURL(value) ? profileURLGuidance(campaignPlatform, true) : null;
    }
    case 'engagement_rate':
    case 'avg_views': {
      if (required === false && value.trim() === '') return null;
      const n = parseAmount(value, 'metric');
      return n !== null && n > 0 ? null : 'Enter a number greater than 0.';
    }
    case 'first_name':
    case 'last_name':
      return value.length > MAX_NAME_LENGTH ? `Enter ${MAX_NAME_LENGTH} characters or fewer.` : null;
    default:
      return looksLikeUnreadablePriceClient(key, value, campaignPlatform)
        ? 'Enter a plain amount such as 12000 or 12k, or enter remove to clear this deliverable.'
        : null;
  }
}

/** "Needs fixing" tab rows: any row with validation errors, whether or not
 *  it's also part of a duplicate group. A VALID duplicate-group member is
 *  still excluded — it only needs duplicate-resolution, not fixing — but an
 *  INVALID one must appear here too (in addition to its non-pickable listing
 *  in the Duplicates tab), otherwise an all-invalid duplicate group has no
 *  member reachable for fixing and the group can never be resolved (spec §5
 *  edge case 3). Excludes a row already dropped: "Fix here" on a dropped row
 *  round-trips sheetResolution 'drop' -> 'fixed' server-side, silently
 *  un-dropping it back into its duplicate group. */
export function needsFixingRows(rows: ImportBatchRow[]): ImportBatchRow[] {
  return rows.filter((r) => r.sheetResolution !== 'drop' && Object.keys(r.validationErrors).length > 0);
}

/** True if a row still needs an explicit sheet-level decision (duplicate or
 *  invalid) — mirrors the backend's validateReadyForRosterReview exactly,
 *  not just "some resolution is set": a row can be stamped sheetResolution
 *  'fixed' by the "Fix here" flow and STILL carry validationErrors if the
 *  fix was itself invalid, and a duplicate-group member can be stamped
 *  anything other than 'keep' (e.g. a re-fix leaves it at 'fixed', not
 *  'keep') without actually resolving which row in the group survives. Both
 *  must be checked directly, not inferred from "resolution is non-empty". */
export function needsSheetDecision(row: ImportBatchRow): boolean {
  if (row.sheetResolution === 'drop') return false;
  if (Object.keys(row.validationErrors).length > 0) return true;
  return row.duplicateGroupSize > 1 && row.sheetResolution !== 'keep';
}

/** Continue (to roster review) is disabled until every row that needs a
 *  sheet-level decision has one. */
export function isSheetReviewComplete(rows: ImportBatchRow[]): boolean {
  return rows.every((r) => !needsSheetDecision(r));
}

/** "Undecided" tab rows for the sheet-issues step — the cross-cutting "what
 *  still needs my attention" filter (spans Duplicates and Needs fixing). */
export function sheetUndecidedRows(rows: ImportBatchRow[]): ImportBatchRow[] {
  return rows.filter(needsSheetDecision);
}

/** Count of distinct sheet issues still needing a decision, for the
 *  "Undecided" tab badge. A duplicate GROUP counts once no matter how many
 *  rows share the username (2, 3, or more) — one decision ("pick a keeper")
 *  resolves the whole group at once — matching the Duplicates tab's own
 *  group-based count instead of double/triple-counting each member row. A
 *  needs-fixing row outside any unresolved duplicate group still counts once
 *  per row, since each is its own separate fix. */
export function sheetUndecidedCount(rows: ImportBatchRow[]): number {
  const undecidedGroupCount = [...duplicateGroups(rows).values()].filter(
    (members) => !members.some((m) => m.sheetResolution === 'keep'),
  ).length;
  const undecidedFixingCount = needsFixingRows(rows).filter(needsSheetDecision).length;
  return undecidedGroupCount + undecidedFixingCount;
}

/** Whether the sheet ever had anything to review — structural, not
 *  resolution-based (a row stays counted here after it's resolved, including
 *  a row that WAS invalid but got fixed: a successful fix clears
 *  validationErrors, so checking that alone would forget the row ever needed
 *  attention the moment it's fixed — sheetResolution 'fixed' is the durable
 *  signal). Used by the step tracker to decide whether "Sheet Review"
 *  belongs in the phase list at all, not whether it still needs attention
 *  (see needsSheetDecision for that). Mirrors packages/features'
 *  importWizardSkipRules.ts version — duplicated rather than imported across
 *  the business-logic/presentation boundary, per this codebase's established
 *  layering convention. */
export function hadSheetIssues(rows: ImportBatchRow[]): boolean {
  return rows.some(
    (r) => r.duplicateGroupSize > 1 || Object.keys(r.validationErrors).length > 0 || r.sheetResolution === 'fixed',
  );
}

/** Rows matched to an existing roster entry during advance-to-roster-review
 *  — these need an explicit Keep existing / Overwrite decision. */
export function conflictRows(rows: ImportBatchRow[]): ImportBatchRow[] {
  return rows.filter((r) => r.rosterMatch !== null);
}

/** Rows the server already defaulted to 'add' (spec §3.2) — shown as
 *  informational-only on the Successful tab. Filtered by resolution, not by
 *  rosterMatch === null: a dropped row also has no rosterMatch but must not
 *  be shown as "will be added" since it won't be. */
export function successfulRows(rows: ImportBatchRow[]): ImportBatchRow[] {
  return rows.filter((r) => r.rosterResolution === 'add');
}

/** Continue (to confirm) is disabled until every conflict row has an
 *  explicit resolution. */
export function isRosterReviewComplete(rows: ImportBatchRow[]): boolean {
  return conflictRows(rows).every(
    (r) => r.rosterResolution === 'keep_existing' || r.rosterResolution === 'overwrite' || r.rosterResolution === 'skip',
  );
}

/** "Undecided" tab rows for the roster-conflicts step — conflict rows with
 *  no resolution chosen yet. A "New" row is never undecided: the server
 *  already defaults it to 'add' (spec §3.2). */
export function rosterUndecidedRows(rows: ImportBatchRow[]): ImportBatchRow[] {
  return conflictRows(rows).filter((r) => r.rosterResolution === '');
}

export interface ImportTally {
  toAdd: number;
  toUpdate: number;
  toKeep: number;
  toSkip: number;
  /** Rows the commit endpoint will actually write. Excludes toSkip AND
   *  toKeep — the backend's rowsToParsedRows excludes dropped rows and
   *  RosterResolution "skip"/"keep_existing" (neither ever touches the
   *  roster). Counting toKeep here would let an all-"keep existing" batch
   *  show a positive tally, enable Commit, and then hit the backend's
   *  "nothing to import" 400 with no way back (confirm has no undo). */
  total: number;
}

/** Final tally for the confirm step, computed from the resolved row snapshot
 *  after both review stages. `total === 0` is the "Nothing to import" gate
 *  (spec §5 edge case 6). */
export function computeImportTally(rows: ImportBatchRow[]): ImportTally {
  const toAdd = rows.filter((r) => r.rosterResolution === 'add').length;
  const toUpdate = rows.filter((r) => r.rosterResolution === 'overwrite').length;
  const toKeep = rows.filter((r) => r.rosterResolution === 'keep_existing').length;
  const toSkip = rows.filter((r) => r.rosterResolution === 'skip').length;
  return { toAdd, toUpdate, toKeep, toSkip, total: toAdd + toUpdate };
}

export interface ConfirmRowSummary {
  rowNumber: number;
  username: string;
  verb: 'add' | 'update' | 'keep' | 'skip';
  /** One-line description of what actually happens to this row — for
   *  `update`, the specific fields changing (current -> new); a fixed
   *  phrase for the other three verbs. */
  detail: string;
}

/** Per-row confirm-step summary — one entry per row with a real
 *  RosterResolution (a sheet-dropped row never gets one and is correctly
 *  absent here, same rows `computeImportTally` counts). For an `overwrite`
 *  row, the detail lists only the fields that actually differ between the
 *  current roster value and the sheet's incoming value — an unlocked match
 *  with identical values shouldn't claim a change that isn't real. Deliverable
 *  price fields are excluded from a locked match's diff entirely (same rule
 *  as `rosterComparisonFields`): Overwrite never touches them, so listing a
 *  "90000 -> —" price change here would describe something that can't
 *  actually happen. first_name/last_name are excluded too — see
 *  NOT_COMPARABLE_FIELDS. */
export function confirmRowSummaries(rows: ImportBatchRow[], expectedFields: ExpectedField[]): ConfirmRowSummary[] {
  return rows.flatMap((r): ConfirmRowSummary[] => {
    const username = r.values.username || r.usernameKey || '—';
    switch (r.rosterResolution) {
      case 'add':
        return [{ rowNumber: r.rowNumber, username, verb: 'add', detail: 'New creator' }];
      case 'overwrite': {
        const locked = r.rosterMatch?.locked ?? false;
        const current = r.rosterMatch?.currentValues ?? {};
        // A field the merge keeps from the roster (rosterKeepFields) commits
        // the CURRENT value, not the sheet's — so it isn't a change and must be
        // excluded from the diff, even though r.values still holds the sheet
        // value for it (Values is never mutated during review).
        const keepFields = new Set(r.rosterKeepFields ?? []);
        // Same rule as rosterComparisonFields: a blank sheet side writes
        // nothing, so it is not a change to list.
        const changes = expectedFields
          .filter((f) => !NOT_COMPARABLE_FIELDS.has(f.key) && !(locked && f.isDeliverable) && !keepFields.has(f.key))
          .filter((f) => Boolean(r.values[f.key]) && (current[f.key] || '') !== r.values[f.key])
          .map((f) => `${compactFieldLabel(f)} ${current[f.key] || '—'} → ${r.values[f.key] || '—'}`);
        return [{ rowNumber: r.rowNumber, username, verb: 'update', detail: changes.length > 0 ? changes.join(' · ') : 'No field changes' }];
      }
      case 'keep_existing':
        return [{ rowNumber: r.rowNumber, username, verb: 'keep', detail: 'Left as-is' }];
      case 'skip':
        return [{ rowNumber: r.rowNumber, username, verb: 'skip', detail: 'Not imported' }];
      default:
        return [];
    }
  });
}
